// Accordion Code created by Jonathan Dallas - 2019
// Version 1.2.0
// breakpoint can be passed as argument
// initializing component more efficiently
//
// Version 1.1.1
// Version 1.2.1 // Added new collapse and expand methods - Jonathan

// Expected markup.
// The link and content need to be nested within the js-accordion element, but can be placed anywhere in the code.
//
// <div class="js-accordion is-open">
//   <div>
//     <button class="js-accordion-link" type="button" aria-expanded="true"><span>View all</span></button>
//   </div>
//   <div class="js-accordion-content">
//       Content to show and hide
//   </div>
// </div>

export function collapseSection(element, height = 0) {
  element.dispatchEvent(new Event("transitionend"));
  // get the height of the element's inner content, regardless of its actual size
  var sectionHeight = element.scrollHeight;

  element.style.overflow = "hidden";
  element.style.height = `${sectionHeight}px`;

  // on the next frame (as soon as the previous style change has taken effect),
  // have the element transition to height: 0
  requestAnimationFrame(() => {
    element.style.height = `${height}px`;

    if (height === 0) {
      element.setAttribute("aria-hidden", true);
      element.style.visibility = "hidden";
    }
  });

  // mark the section as "currently collapsed"
  element.setAttribute("data-collapsed", "true");
}

export function expandSection(element) {
  // get the height of the element's inner content, regardless of its actual size
  var sectionHeight = element.scrollHeight;

  // have the element transition to the height of its inner content
  element.style.height = sectionHeight + "px";
  element.style.visibility = "visible";

  const expandedDone = (e) => {
    // remove this event listener so it only gets triggered once
    element.removeEventListener("transitionend", expandedDone);

    // remove "height" from the element's inline styles, so it can return to its initial value
    element.style.height = null;
    element.removeAttribute("aria-hidden"); // existence of aria-hidden makes it hidden
    element.style.overflow = "unset";
  };

  // when the next css transition finishes (which should be the one we just triggered)
  if (element.style.transition) {
    element.addEventListener("transitionend", expandedDone);
  } else {
    expandedDone();
  }

  // mark the section as "currently not collapsed"
  element.setAttribute("data-collapsed", "false");
}

export function initAccordions() {
  Array.from(document.querySelectorAll(".js-accordion")).forEach(initAccordion);
}

export default function initAccordion(el, id, breakpoint = null) {
  const link = el.querySelector(".js-accordion-link");
  const content = el.querySelector(".js-accordion-content");

  if (!link || !content) {
    return false;
  }

  // check if we're in the experience editor to force the content open and prevent the binding event for the link.
  const editorView = document.querySelector(".is-page-editor");
  const animationSpeed = 500;

  const contentWrapper = wrap(content, document.createElement("div"));

  breakpoint = breakpoint || el.getAttribute("data-accordion-breakpoint");

  let expanded; // do not set to an initial value that is done be the ToggleContent
  let isMobile;

  // if in Experience Editor open accordions and then exit.
  if (editorView) {
    toggleContent(true);
    // adding the wrapper class will show the content
    contentWrapper.classList.add("accordion-wrapper");
    return;
  }

  if (breakpoint) {
    // initialize this backwards so the checkMobile method works properly.
    isMobile = window.innerWidth > breakpoint;

    checkMobileOnlyAccordions();

    window.addEventListener("resize", checkMobileOnlyAccordions, {
      passive: true,
    });
  } else {
    toggleContent(!!el.classList.contains("is-open"));
  }

  link.addEventListener("click", (e) => {
    e.preventDefault();
    toggleContent(!expanded);
  });

  // initialize the Link's attributes
  link.setAttribute("id", `accordion-link-${id}`);
  link.setAttribute("aria-controls", `accordion-content-${id}`);

  // initialize the Content Wrapper after the expanded state is set to avoid transitions
  contentWrapper.classList.add("accordion-wrapper");
  contentWrapper.setAttribute("id", `accordion-content-${id}`);
  contentWrapper.setAttribute("aria-labelledby", `accordion-link-${id}`);
  contentWrapper.style.transition = `height ${animationSpeed}ms ease, visibility ${animationSpeed}ms ease`;

  function toggleContent(openState) {
    if (openState === expanded || (!openState && !isToggleable())) {
      return;
    }

    expanded = openState;

    link.setAttribute("aria-expanded", openState);

    if (expanded) {
      expandSection(contentWrapper);
      el.classList.add("is-open");
    } else {
      collapseSection(contentWrapper);
      el.classList.remove("is-open");
    }
  }

  function wrap(el, wrapper) {
    el.parentNode.insertBefore(wrapper, el);
    wrapper.appendChild(el);
    return wrapper;
  }

  // helper function to check if mobile-only accordions should toggle their state
  // non-mobile-only will always toggle; mobile-only should only toggle at
  // smartphone widths or narrower (470px matches the CSS breakpoint)
  function isToggleable() {
    return !breakpoint || (breakpoint && window.innerWidth <= breakpoint);
  }

  function checkMobileOnlyAccordions() {
    if (isMobile && window.innerWidth > breakpoint) {
      link.setAttribute("disabled", "");
      toggleContent(true);
      isMobile = false;
    }

    if (!isMobile && window.innerWidth <= breakpoint) {
      link.removeAttribute("disabled");
      toggleContent(false);
      isMobile = true;
    }
  }

  return {
    el,
    toggleContent,
  };
}
